/**
 * Automation Shared Invariants
 *
 * Enforces:
 * 1. Permissions - source-based command access control
 *
 * No normalize needed (simple state, no computed fields).
 * No validate needed (no form validation).
 */

import { Invariants } from '@commandDrivenStateEngine/core/invariants';
import { AutomationSharedState } from './state';
import { AutomationSharedCommand } from './commands';

/**
 * Permission control - source-based command access.
 *
 * Permission matrix:
 * - user: dropdown/open, dropdown/close
 * - api: data/*, loading/*
 * - ai: dropdown/open, dropdown/close
 * - url: nothing allowed
 */
const canApply = ({
  command,
  meta
}: {
  state: AutomationSharedState;
  command: AutomationSharedCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // API source: data and loading commands only
  if ( source === 'api' ) {
    return (
      type.startsWith( 'automation/shared/data/' ) ||
      type.startsWith( 'automation/shared/loading/' )
    );
  }

  // User source: dropdown commands only
  if ( source === 'user' ) {
    return type.startsWith( 'automation/shared/dropdown/' );
  }

  // AI source: dropdown commands only
  if ( source === 'ai' ) {
    return type.startsWith( 'automation/shared/dropdown/' );
  }

  // URL source: nothing allowed
  if ( source === 'url' ) {
    return false;
  }

  // Unknown sources: allow by default (don't block)
  return true;
};

export const invariants: Invariants<AutomationSharedState, AutomationSharedCommand> = {
  canApply
};
